import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, STAFF_ROLES } from "@/context/AuthContext";
import PublicLayout from "@/components/PublicLayout";
import PortalLayout from "@/components/PortalLayout";
import { Loading } from "@/components/common";

import Home from "@/pages/public/Home";
import Services from "@/pages/public/Services";
import Pricing from "@/pages/public/Pricing";
import TaxRescue from "@/pages/public/TaxRescue";
import Resources from "@/pages/public/Resources";
import Consultation from "@/pages/public/Consultation";
import Legal from "@/pages/public/Legal";
import Login from "@/pages/public/Login";
import Register from "@/pages/public/Register";
import ResetPassword from "@/pages/public/ResetPassword";
import PaymentResult from "@/pages/public/PaymentResult";

import Dashboard from "@/pages/portal/Dashboard";
import TaxVault from "@/pages/portal/TaxVault";
import Sales from "@/pages/portal/Sales";
import Reconciliation from "@/pages/portal/Reconciliation";
import Filings from "@/pages/portal/Filings";
import Payments from "@/pages/portal/Payments";
import Documents from "@/pages/portal/Documents";
import TaxCalendar from "@/pages/portal/TaxCalendar";
import Notifications from "@/pages/portal/Notifications";
import Reports from "@/pages/portal/Reports";
import YearEnd from "@/pages/portal/YearEnd";
import Support from "@/pages/portal/Support";
import PortalSettings from "@/pages/portal/PortalSettings";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCustomerDetail from "@/pages/admin/AdminCustomerDetail";
import AdminWorkQueue from "@/pages/admin/AdminWorkQueue";
import AdminTaxRules from "@/pages/admin/AdminTaxRules";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminRescue from "@/pages/admin/AdminRescue";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import AdminRevenue from "@/pages/admin/AdminRevenue";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminSettings from "@/pages/admin/AdminSettings";
import TaxProDashboard from "@/pages/admin/TaxProDashboard";

function RequireAuth({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading label="Authenticating" />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function RequireStaff({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (!STAFF_ROLES.includes(user.role)) return <Navigate to="/portal" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/tax-rescue" element={<TaxRescue />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/consultation" element={<Consultation />} />
              <Route path="/legal/:slug" element={<Legal />} />
            </Route>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/payment/success" element={<PaymentResult ok />} />
            <Route path="/payment/cancel" element={<PaymentResult />} />

            <Route element={<RequireAuth><PortalLayout /></RequireAuth>}>
              <Route path="/portal" element={<Dashboard />} />
              <Route path="/portal/vault" element={<TaxVault />} />
              <Route path="/portal/sales" element={<Sales />} />
              <Route path="/portal/reconciliation" element={<Reconciliation />} />
              <Route path="/portal/filings" element={<Filings />} />
              <Route path="/portal/payments" element={<Payments />} />
              <Route path="/portal/documents" element={<Documents />} />
              <Route path="/portal/calendar" element={<TaxCalendar />} />
              <Route path="/portal/notifications" element={<Notifications />} />
              <Route path="/portal/reports" element={<Reports />} />
              <Route path="/portal/year-end" element={<YearEnd />} />
              <Route path="/portal/support" element={<Support />} />
              <Route path="/portal/settings" element={<PortalSettings />} />
              <Route path="/admin" element={<RequireStaff><AdminDashboard /></RequireStaff>} />
              <Route path="/admin/customers" element={<RequireStaff><AdminCustomers /></RequireStaff>} />
              <Route path="/admin/customers/:id" element={<RequireStaff><AdminCustomerDetail /></RequireStaff>} />
              <Route path="/admin/work-queue" element={<RequireStaff><AdminWorkQueue /></RequireStaff>} />
              <Route path="/admin/tax-rules" element={<RequireStaff><AdminTaxRules /></RequireStaff>} />
              <Route path="/admin/plans" element={<RequireStaff><AdminPlans /></RequireStaff>} />
              <Route path="/admin/rescue" element={<RequireStaff><AdminRescue /></RequireStaff>} />
              <Route path="/admin/integrations" element={<RequireStaff><AdminIntegrations /></RequireStaff>} />
              <Route path="/admin/revenue" element={<RequireStaff><AdminRevenue /></RequireStaff>} />
              <Route path="/admin/audit" element={<RequireStaff><AdminAudit /></RequireStaff>} />
              <Route path="/admin/settings" element={<RequireStaff><AdminSettings /></RequireStaff>} />
              <Route path="/taxpro" element={<RequireStaff><TaxProDashboard /></RequireStaff>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
